import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { LEVEL_STYLES, LABEL_LOOKUP, LOCATION_OPTIONS, DEVICE_OPTIONS, KIND_OPTIONS } from "@/lib/enums";
import { t, LANG } from "@/lib/i18n";

const SharedRecord = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    axios.get(`${API}/share/${token}`).then(
      (r) => setData(r.data),
      (e) => setErr(t(e.response?.data?.detail || "This shared record is not available.")),
    );
  }, [token]);

  if (err) return (
    <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="shared-record-error">
      <p className="eyebrow">{t("Shared Radon Record")}</p>
      <h1 className="font-serif text-3xl mt-2 text-[#1C201F]">{t("Not available")}</h1>
      <p className="text-sm text-[#5C6661] mt-3">{err}</p>
    </div>
  );

  if (!data) return (
    <div className="max-w-2xl mx-auto px-6 py-24 text-center eyebrow">{t("Loading…")}</div>
  );

  const latest = data.readings[0];
  const c = latest?.classification;
  const s = c ? LEVEL_STYLES[c.level] : null;

  return (
    <div className="max-w-4xl mx-auto px-6 lg:px-8 py-12 space-y-8" data-testid="shared-record">
      <div className="fade-up">
        <p className="eyebrow">{t("Shared Radon Record")}</p>
        <h1 className="font-serif text-4xl sm:text-5xl mt-2 text-[#1C201F]">
          {LANG === "es" ? (
            <><span className="italic text-[#2F524A]">Casa de </span>{data.owner_name}.</>
          ) : (
            <>{data.owner_name}<span className="italic text-[#2F524A]">’s home.</span></>
          )}
        </h1>
        <p className="text-sm text-[#5C6661] mt-2">
          {data.property.street}, {data.property.city}, {data.property.state} {data.property.zip_code}
        </p>
      </div>

      {latest && (
        <div className="card-flat" style={{ background: s.bg, borderColor: s.border }} data-testid="shared-latest">
          <p className="eyebrow" style={{ color: s.text }}>{t("Radon Action Level:")}{" "}{t(c.label)}</p>
          <div className="mt-3 flex items-baseline gap-3">
            <span className="font-serif text-6xl leading-none text-[#1C201F]">{latest.pci_l.toFixed(1)}</span>
            <span className="text-lg text-[#5C6661]">{t("pCi/L")}</span>
          </div>
          <p className="mt-4 text-base text-[#1C201F]">{t(c.headline)}</p>
          <p className="mt-2 text-sm text-[#5C6661] leading-relaxed">{t(c.summary)}</p>
        </div>
      )}

      {data.mitigation && (
        <div className="card-flat" data-testid="shared-mitigation">
          <h3 className="font-serif text-xl">{t("Mitigation completed")}</h3>
          <dl className="mt-3 grid grid-cols-2 gap-y-2 gap-x-4 text-sm">
            <dt className="text-[#5C6661]">{t("Date")}</dt><dd className="text-[#1C201F]">{data.mitigation.completed_date}</dd>
            <dt className="text-[#5C6661]">{t("Contractor")}</dt><dd className="text-[#1C201F]">{data.mitigation.contractor}</dd>
            <dt className="text-[#5C6661]">{t("System")}</dt><dd className="text-[#1C201F]">{data.mitigation.system_type ? t(data.mitigation.system_type) : "—"}</dd>
          </dl>
        </div>
      )}

      <div className="card-flat">
        <h3 className="font-serif text-xl">{t("Testing history")}</h3>
        {data.readings.length === 0 ? (
          <p className="text-sm text-[#5C6661] mt-3">{t("No readings yet.")}</p>
        ) : (
          <table className="w-full text-sm mt-4">
            <thead>
              <tr className="text-left text-[10px] tracking-[0.18em] uppercase text-[#5C6661] border-b border-[#E8ECEA]">
                <th className="py-2 pr-3">{t("Date")}</th><th className="py-2 pr-3">{t("Location")}</th>
                <th className="py-2 pr-3">{t("Reading")}</th><th className="py-2 pr-3">{t("Device")}</th>
                <th className="py-2">{t("Kind")}</th>
              </tr>
            </thead>
            <tbody>
              {data.readings.map((r) => (
                <tr key={r.id} className="border-b border-[#E8ECEA]/60">
                  <td className="py-3 pr-3 text-[#1C201F]">{r.reading_date}</td>
                  <td className="py-3 pr-3 text-[#5C6661]">{LABEL_LOOKUP(LOCATION_OPTIONS, r.location_in_home)}</td>
                  <td className="py-3 pr-3"><span className="font-serif text-base text-[#1C201F]">{r.pci_l.toFixed(1)}</span> <span className="text-[10px] text-[#5C6661]">{t("pCi/L")}</span></td>
                  <td className="py-3 pr-3 text-[#5C6661]">{LABEL_LOOKUP(DEVICE_OPTIONS, r.device_type)}</td>
                  <td className="py-3 text-[#5C6661]">{LABEL_LOOKUP(KIND_OPTIONS, r.kind)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <p className="text-xs text-[#5C6661] leading-relaxed">
        This record is shared read-only. The EPA action level is 4.0 pCi/L; no
        level of radon is completely safe.
      </p>
    </div>
  );
};

export default SharedRecord;
